// src/services/firestoreService.js
// ─────────────────────────────────────────────
// All Firestore read/write operations live here.
// Components import from here — not directly from firebase.
// ─────────────────────────────────────────────
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  onSnapshot,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { mergeJobPrivate, splitJobForStorage, toCoarseArea } from '../utils/jobPrivacy';

// ─── NOTIFICATIONS (declared early — used by jobs/bids) ──────────────────────

export const createNotification = async (userId, type, message, jobId) => {
  await addDoc(collection(db, 'notifications'), {
    userId,
    type,
    message,
    jobId: jobId || null,
    read:      false,
    createdAt: serverTimestamp(),
  });
};

// ─── WEEKLY LEAGUE (declared early — used by bids/reviews) ───────────────────

// Monday of this week as YYYY-MM-DD, worked out in UTC so it matches the Cloud
// Functions that now write weeklyLeague (functions/lib/gamificationLogic.js).
// The old local-time version gave Sunday's date during British Summer Time.
const weekStartUTC = (offsetWeeks = 0) => {
  const now = new Date();
  const day = now.getUTCDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const monday = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diff + offsetWeeks * 7
  ));
  return monday.toISOString().split('T')[0];
};

const getCurrentWeekStart = () => weekStartUTC(0);

export { getCurrentWeekStart };

export const getPreviousWeekStart = () => weekStartUTC(-1);

export const getPreviousWeekLeague = async () => {
  const prevWeekStart = getPreviousWeekStart();
  const snap = await getDocs(collection(db, 'weeklyLeague'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(e => e.weekStart === prevWeekStart)
    .sort((a, b) => b.points - a.points);
};

export const getWeeklyLeagueWinner = async () => {
  const prevLeague = await getPreviousWeekLeague();
  return prevLeague[0] || null;
};

// League points, rival matching and the weekly reset are written only by Cloud
// Functions now (functions/gamification.js, weeklyLeagueReset). Clients can read
// weeklyLeague but no longer write it.

// Kept so existing callers keep working: rivals are assigned server-side after
// every accepted bid and at the weekly reset.
export const ensureRivalsAssigned = async () => {};

export const getWeeklyLeague = async () => {
  const snap = await getDocs(collection(db, 'weeklyLeague'));
  const weekStart = getCurrentWeekStart();
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(e => e.weekStart === weekStart)
    .sort((a, b) => b.points - a.points);
};

// Real-time listener for the current week's league — Yard tab updates live as points change
export const listenWeeklyLeague = (callback) => {
  const weekStart = getCurrentWeekStart();
  return onSnapshot(
    collection(db, 'weeklyLeague'),
    snap => {
      const league = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(e => e.weekStart === weekStart)
        .sort((a, b) => b.points - a.points);
      callback(league);
    },
    err => { console.error('[listenWeeklyLeague] snapshot error:', err.code, err.message); }
  );
};

// ─── JOBS ─────────────────────────────────────

// Post a new job — notifies all verified carriers.
// Street address, full postcode, and customer name/photo go to
// jobs/{id}/private/details. The public doc only stores a coarse area.
export const postJob = async (jobData, userId) => {
  const isScrap = jobData.serviceType === 'Scrap Collection';
  const { publicFields, privateFields } = splitJobForStorage(jobData);
  const ref = await addDoc(collection(db, 'jobs'), {
    ...publicFields,
    ...(isScrap ? { budget: '0', price: 0 } : {}),
    customerId: userId,
    status:     'Pending',
    bidCount:   0,
    createdAt:  serverTimestamp(),
  });

  try {
    await setDoc(doc(db, 'jobs', ref.id, 'private', 'details'), privateFields);
  } catch (err) {
    console.error('[postJob] private details write failed:', err.code, err.message);
    try { await updateDoc(ref, { status: 'Cancelled' }); } catch (cancelErr) {
      console.error('[postJob] could not cancel job after private write failure:', cancelErr.message);
    }
    throw err;
  }

  // Notify every verified carrier — area only, never the street or full postcode.
  // The job is already saved. A failure here must not be reported as a failed post,
  // or the customer retries and creates a duplicate.
  try {
    const carriersSnap = await getDocs(
      query(collection(db, 'publicCarriers'), where('verified', '==', true))
    );
    const area = publicFields.pickupArea || 'your area';
    const message = `New job posted: ${jobData.serviceType || 'Job'} in ${area}`;
    await Promise.all(
      carriersSnap.docs.map(d =>
        createNotification(d.id, 'new_job', message, ref.id)
      )
    );
    console.log(`[postJob] new_job notifications sent to ${carriersSnap.docs.length} verified carrier(s) for job ${ref.id}`);
  } catch (err) {
    console.error('[postJob] carrier notification failed:', err.code, err.message, err);
  }

  return ref.id;
};

// Get all open jobs (for provider to browse) — one-time fetch (kept for legacy use)
export const getOpenJobs = async () => {
  const q = query(
    collection(db, 'jobs'),
    where('status', '==', 'Pending'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Real-time listener for open jobs — fires on every add/update/remove
export const listenToOpenJobs = (callback) => {
  const q = query(
    collection(db, 'jobs'),
    where('status', '==', 'Pending'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    snap => { callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
    err  => { console.error('[listenToOpenJobs] snapshot error:', err.code, err.message); }
  );
};

const attachPrivateDetails = async (job) => {
  try {
    const priv = await getDoc(doc(db, 'jobs', job.id, 'private', 'details'));
    return mergeJobPrivate(job, priv.exists() ? priv.data() : null);
  } catch (err) {
    console.error('[attachPrivateDetails] failed:', err.code, err.message);
    return job;
  }
};

// Get jobs for a specific customer (one-time). Merges private address for the owner.
export const getCustomerJobs = async (customerId) => {
  const q = query(
    collection(db, 'jobs'),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  const jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return Promise.all(jobs.map(attachPrivateDetails));
};

// Real-time listener for a customer's jobs — updates the history list live.
// Also subscribes to each job's private details so the customer sees their address.
export const listenCustomerJobs = (customerId, callback) => {
  const q = query(
    collection(db, 'jobs'),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const privateUnsubs = new Map();
  const overlays = new Map();
  let publicDocs = [];

  const emit = () => {
    callback(publicDocs.map(job => mergeJobPrivate(job, overlays.get(job.id) || null)));
  };

  const unsub = onSnapshot(
    q,
    snap => {
      const nextIds = new Set(snap.docs.map(d => d.id));
      publicDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      publicDocs.forEach(job => {
        if (privateUnsubs.has(job.id)) return;
        privateUnsubs.set(job.id, onSnapshot(
          doc(db, 'jobs', job.id, 'private', 'details'),
          ps => {
            overlays.set(job.id, ps.exists() ? ps.data() : null);
            emit();
          },
          err => { console.error('[listenCustomerJobs] private error:', err.code, err.message); }
        ));
      });

      [...privateUnsubs.keys()].forEach(id => {
        if (nextIds.has(id)) return;
        privateUnsubs.get(id)();
        privateUnsubs.delete(id);
        overlays.delete(id);
      });

      emit();
    },
    err => { console.error('[listenCustomerJobs] snapshot error:', err.code, err.message); }
  );

  return () => {
    unsub();
    privateUnsubs.forEach(unsubPrivate => unsubPrivate());
    privateUnsubs.clear();
  };
};

// Update job status — notifies customer on En Route / Arrived / Completed
export const updateJobStatus = async (jobId, status) => {
  await updateDoc(doc(db, 'jobs', jobId), { status });
  if (status === 'En Route' || status === 'Arrived' || status === 'Completed') {
    const jobSnap = await getDoc(doc(db, 'jobs', jobId));
    if (jobSnap.exists()) {
      const { customerId } = jobSnap.data();
      if (status === 'En Route' || status === 'Arrived') {
        const msg = status === 'En Route'
          ? 'Your carrier is on the way!'
          : 'Your carrier has arrived!';
        await createNotification(customerId, status.toLowerCase().replace(' ', '_'), msg, jobId);
      } else if (status === 'Completed' && customerId) {
        await createNotification(
          customerId,
          'job_completed',
          '✅ Your carrier has marked the job as complete. Please confirm and leave a review.',
          jobId
        );
      }
    }
  }
};

// Real-time listener for a single public job doc (status, payment, coarse area).
export const listenToJob = (jobId, callback) => {
  return onSnapshot(doc(db, 'jobs', jobId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
};

// Public job plus private address. Only succeeds for the customer and the accepted carrier.
export const listenToJobWithPrivate = (jobId, callback) => {
  let publicJob = null;
  let privateDetails = null;
  const emit = () => {
    if (!publicJob) return;
    callback(mergeJobPrivate(publicJob, privateDetails));
  };
  const unsubJob = onSnapshot(
    doc(db, 'jobs', jobId),
    snap => {
      publicJob = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      emit();
    },
    err => { console.error('[listenToJobWithPrivate] job error:', err.code, err.message); }
  );
  const unsubPrivate = onSnapshot(
    doc(db, 'jobs', jobId, 'private', 'details'),
    snap => {
      privateDetails = snap.exists() ? snap.data() : null;
      emit();
    },
    err => { console.error('[listenToJobWithPrivate] private error:', err.code, err.message); }
  );
  return () => {
    unsubJob();
    unsubPrivate();
  };
};

// ─── BIDS ─────────────────────────────────────

// Get bids for a job ordered by price ascending (one-time fetch)
export const getJobBids = async (jobId) => {
  const snap = await getDocs(query(collection(db, 'bids'), where('jobId', '==', jobId)));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b => !b.status || b.status === 'Offered')
    .sort((a, b) => a.price - b.price);
};

// Real-time listener for all bids on a job, ordered by Eco Credits desc (visibility reward)
export const listenToJobBids = (jobId, callback) => {
  const q = query(collection(db, 'bids'), where('jobId', '==', jobId));
  return onSnapshot(q, snap => {
    const bids = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(b => !b.status || b.status === 'Offered')
      .sort((a, b) => {
        const credDiff = (b.credits || 0) - (a.credits || 0);
        if (credDiff !== 0) return credDiff;
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      });
    callback(bids);
  });
};

// Minimum bid for priced (non-Scrap) jobs — matches the £20 minimum enforced by
// createPaymentIntent, so every accepted priced bid can actually be paid.
export const MIN_BID_GBP = 20;

// Submit a bid
export const submitBid = async (bidData, providerId) => {
  const targetJob = await getDoc(doc(db, 'jobs', bidData.jobId));
  const isScrapJob = targetJob.exists() && targetJob.data().serviceType === 'Scrap Collection';
  if (!isScrapJob && !(Number(bidData.price) >= MIN_BID_GBP)) {
    throw new Error(`Minimum bid is £${MIN_BID_GBP}`);
  }
  const ref = await addDoc(collection(db, 'bids'), {
    ...bidData,
    providerId,
    status:    'Offered',
    createdAt: serverTimestamp(),
  });
  // Secondary operations — bid is already saved; these failing must not throw to caller
  try {
    const jobRef  = doc(db, 'jobs', bidData.jobId);
    const jobSnap = await getDoc(jobRef);
    if (jobSnap.exists()) {
      const prevBidCount = jobSnap.data().bidCount || 0;
      await updateDoc(jobRef, { bidCount: prevBidCount + 1 });
      await createNotification(
        jobSnap.data().customerId,
        'new_bid',
        'A carrier has bid on your job',
        bidData.jobId
      );
      // totalBids, the fastest-bid bonus (+2 league points, speedBonus flag) and the
      // bid's credits/rating/jobs are set by the onBidCreated Cloud Function.
      // Notify other carriers already bidding on this job
      const otherBidsSnap = await getDocs(
        query(collection(db, 'bids'), where('jobId', '==', bidData.jobId))
      );
      const rawArea = jobSnap.data().pickupArea || jobSnap.data().pickup || jobSnap.data().location || '';
      const location = toCoarseArea(rawArea) || 'your area';
      await Promise.all(
        otherBidsSnap.docs
          .filter(d => d.id !== ref.id && d.data().providerId !== providerId && d.data().status === 'Offered')
          .map(d => createNotification(
            d.data().providerId,
            'outbid',
            `${bidData.providerName} has entered The Yard on your job in ${location}. Check your position!`,
            bidData.jobId
          ))
      );
    }
  } catch (err) {
    console.error('[submitBid] secondary ops failed (bid was saved):', err.code, err.message);
  }
  return ref.id;
};

// Get bids for a job (real-time) — sorted by Eco Credits desc, then earliest bid first
export const listenToBids = (jobId, callback) => {
  const q = query(
    collection(db, 'bids'),
    where('jobId', '==', jobId)
  );
  return onSnapshot(q, (snap) => {
    const bids = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(b => b.status === 'Offered')
      .sort((a, b) => {
        const credDiff = (b.credits || 0) - (a.credits || 0);
        if (credDiff !== 0) return credDiff;
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      });
    callback(bids);
  });
};

// Get bids submitted by a provider (one-time)
export const getProviderBids = async (providerId) => {
  const q = query(
    collection(db, 'bids'),
    where('providerId', '==', providerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Real-time listener for a provider's bids — updates when any bid status changes
export const listenProviderBids = (providerId, callback) => {
  const q = query(
    collection(db, 'bids'),
    where('providerId', '==', providerId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q,
    snap => { callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
    err  => { console.error('[listenProviderBids] snapshot error:', err.code, err.message); }
  );
};

// Get the accepted bid for a specific job
export const getAcceptedBidForJob = async (jobId) => {
  const q = query(
    collection(db, 'bids'),
    where('jobId', '==', jobId),
    where('status', '==', 'Accepted')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

// Accept a bid — updates bid + job and notifies the carrier. Rewards are server-side.
export const acceptBid = async (bidId, jobId, providerId) => {
  await updateDoc(doc(db, 'bids', bidId), { status: 'Accepted' });
  await updateDoc(doc(db, 'jobs', jobId), {
    status:             'Accepted',
    acceptedBidId:      bidId,
    acceptedProviderId: providerId,
  });
  await createNotification(providerId, 'bid_accepted', 'Your bid has been accepted!', jobId);
  // League points, streak, challenge progress, rivalry notifications, rival
  // matching and referral rewards run in the onJobUpdated Cloud Function.
};

// ─── USERS / LEADERBOARD ──────────────────────

// Tie-break for carriers with the same Eco Credits.
// credits desc, then jobs desc, then rating desc, then document id ascending.
// Missing numbers count as 0.
export const compareLeaderboardRows = (a, b) => {
  const credits = (Number(b.credits) || 0) - (Number(a.credits) || 0);
  if (credits) return credits;
  const jobs = (Number(b.jobs) || 0) - (Number(a.jobs) || 0);
  if (jobs) return jobs;
  const rating = (Number(b.rating) || 0) - (Number(a.rating) || 0);
  if (rating) return rating;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
};

// Get leaderboard (top carriers by eco credits). Reads publicCarriers, which holds
// only public fields; carriers' users docs are private to them and admins.
// Firestore can order by credits alone on the automatic single-field index.
// Ordering by credits, jobs, rating and document id together needs a composite
// index on publicCarriers, which this project does not have, so ties are
// re-sorted here among the rows the limit already returned.
export const getLeaderboard = async (limit = 10) => {
  const q = query(
    collection(db, 'publicCarriers'),
    orderBy('credits', 'desc'),
    firestoreLimit(limit)
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  rows.sort(compareLeaderboardRows);
  return rows.map((row, i) => ({ ...row, rank: i + 1 }));
};

// Eco Credits for a completed job are awarded by the onJobUpdated Cloud Function.

// ─── REVIEWS ──────────────────────────────────

// Check whether a review already exists for a given job + customer
export const hasExistingReview = async (jobId, customerId) => {
  const q = query(
    collection(db, 'reviews'),
    where('jobId', '==', jobId),
    where('customerId', '==', customerId)
  );
  const snap = await getDocs(q);
  return !snap.empty;
};

// Submit a review. The carrier's rating and credits are updated server-side.
export const submitReview = async ({ jobId, customerId, providerId, rating, review }) => {
  const already = await hasExistingReview(jobId, customerId);
  if (already) return 'Review already submitted';

  await addDoc(collection(db, 'reviews'), {
    jobId,
    customerId,
    providerId,
    rating,
    review,
    createdAt: serverTimestamp(),
  });
  // Average rating, fiveStarReviews, Eco Credits (+/-), league points, challenge
  // progress and the carrier's notification are handled by onReviewCreated.
};

// Get review count for a provider
export const getProviderReviewCount = async (providerId) => {
  const q = query(collection(db, 'reviews'), where('providerId', '==', providerId));
  const snap = await getDocs(q);
  return snap.size;
};

// Record that the customer deliberately skipped leaving a review (analytics only)
export const markReviewSkipped = async (jobId) => {
  await updateDoc(doc(db, 'jobs', jobId), { reviewSkipped: true });
};

// Real-time listener for a provider's reviews — updates when a new review is submitted
export const listenProviderReviews = (providerId, callback) => {
  const q = query(collection(db, 'reviews'), where('providerId', '==', providerId));
  return onSnapshot(
    q,
    snap => { callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
    err  => { console.error('[listenProviderReviews] snapshot error:', err.code, err.message); }
  );
};

// Get all reviews for a provider (one-time — kept for CarrierProfile public page)
export const getProviderReviews = async (providerId) => {
  const q = query(
    collection(db, 'reviews'),
    where('providerId', '==', providerId)
  );
  const snap = await getDocs(q);
  const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  results.sort((a, b) => b.createdAt - a.createdAt);
  return results;
};

// ─── WASTE TRANSFER NOTES ─────────────────────

export const createWasteTransferNote = async (jobId, providerId, customerId, data) => {
  const ref = await addDoc(collection(db, 'wasteTransferNotes'), {
    jobId,
    providerId,
    customerId,
    ...data,
    createdAt: serverTimestamp(),
    ref:       `WTN-${Date.now().toString().slice(-8)}`,
  });
  return ref.id;
};

export const getWasteTransferNotes = async (customerId) => {
  const q = query(
    collection(db, 'wasteTransferNotes'),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── NOTIFICATIONS (listeners + read management) ─────────────────────────────

export const listenNotifications = (userId, callback) => {
  console.log('[listenNotifications] subscribing for uid:', userId);
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    firestoreLimit(20)
  );
  return onSnapshot(
    q,
    snap => {
      console.log(
        `[listenNotifications] snapshot — ${snap.docs.length} docs,`,
        `fromCache: ${snap.metadata.fromCache},`,
        `hasPendingWrites: ${snap.metadata.hasPendingWrites}`
      );
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    },
    err => {
      console.error('[listenNotifications] snapshot error:', err.code, err.message);
      // If the error is a missing composite index, Firestore will log a URL in the
      // console — open it to create the (userId ASC, createdAt DESC) index.
    }
  );
};

export const markNotificationRead = async (notificationId) => {
  await updateDoc(doc(db, 'notifications', notificationId), { read: true });
};

export const markAllNotificationsRead = async (userId) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('read', '==', false)
  );
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map(d => updateDoc(d.ref, { read: true })));
};

// Live activity feed — last 10 outbid notifications across all carriers
export const listenYardActivity = (callback) => {
  const q = query(
    collection(db, 'notifications'),
    orderBy('createdAt', 'desc'),
    firestoreLimit(50)
  );
  return onSnapshot(q, snap => {
    const activity = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(n => n.type === 'outbid')
      .slice(0, 10);
    callback(activity);
  });
};

// ─── JOB / BID CANCELLATION ──────────────────

export const cancelJob = async (jobId) => {
  await updateDoc(doc(db, 'jobs', jobId), { status: 'Cancelled' });
};

// A Speed Demon bonus on this bid is reversed by the onBidUpdated Cloud Function.
export const withdrawBid = async (bidId) => {
  await updateDoc(doc(db, 'bids', bidId), { status: 'Withdrawn' });
};

// ─── CARRIER STREAK ───────────────────────────────────────────────────────────
// Streaks are updated server-side when a bid is accepted (onJobUpdated).

// ─── MESSAGES ────────────────────────────────
// Schema: { jobId, senderId, senderName, senderType, recipientId, text, read, createdAt }

export const sendMessage = async (jobId, senderId, senderName, senderType, recipientId, text) => {
  await addDoc(collection(db, 'messages'), {
    jobId,
    senderId,
    senderName,
    senderType,
    recipientId,
    text,
    read:      false,
    createdAt: serverTimestamp(),
  });
};

// Real-time listener for one job's chat. Queries are scoped to the signed-in user
// (sender or recipient) because a jobId-only query is rejected by the security rules.
// Messages for other jobs are filtered out client-side. Single-field indexes only.
export const listenToMessages = (jobId, userId, callback) => {
  if (!jobId || !userId) return () => {};
  const sentQ = query(collection(db, 'messages'), where('senderId', '==', userId));
  const recvQ = query(collection(db, 'messages'), where('recipientId', '==', userId));
  let sent = [];
  let received = [];
  const emit = () => {
    const byId = new Map();
    [...sent, ...received].forEach(msg => {
      if (msg.jobId === jobId) byId.set(msg.id, msg);
    });
    const msgs = [...byId.values()]
      .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
    callback(msgs);
  };
  const onErr = (err) => console.error('[listenToMessages] snapshot error:', err.code, err.message);
  const unsubSent = onSnapshot(sentQ, snap => {
    sent = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    emit();
  }, onErr);
  const unsubRecv = onSnapshot(recvQ, snap => {
    received = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    emit();
  }, onErr);
  return () => {
    unsubSent();
    unsubRecv();
  };
};

// Mark all messages sent to userId in a job as read
export const markMessagesRead = async (jobId, userId) => {
  const q = query(collection(db, 'messages'), where('recipientId', '==', userId));
  const snap = await getDocs(q);
  const toMark = snap.docs.filter(d => d.data().jobId === jobId && d.data().read === false);
  await Promise.all(toMark.map(d => updateDoc(d.ref, { read: true })));
};

// Unread message count for a user (for NavBar badge)
export const listenUnreadMessages = (userId, callback) => {
  const q = query(collection(db, 'messages'), where('recipientId', '==', userId));
  return onSnapshot(q, snap => {
    const count = snap.docs.filter(d => d.data().read === false).length;
    callback(count);
  });
};

// ─── IMAGE UPLOADS ────────────────────────────────────────────────────────────
// Schema: jobs/{jobId}/images/{timestamp}_{filename}
// Compresses each Blob to ≤1 MB via canvas before uploading.

export const uploadJobImages = async (jobId, blobs, onProgress) => {
  console.log('[uploadJobImages] Starting — jobId:', jobId, '| files:', blobs.length,
    '| bucket:', storage.app.options.storageBucket);
  const urls = [];
  for (let i = 0; i < blobs.length; i++) {
    const blob = blobs[i];
    const name = blob.name || `image_${i}.jpg`;
    const path = `jobs/${jobId}/images/${Date.now()}_${name}`;
    console.log(`[uploadJobImages] [${i + 1}/${blobs.length}] → ${path} (${blob.size} bytes, type: ${blob.type})`);
    const fileRef = storageRef(storage, path);
    const task    = uploadBytesResumable(fileRef, blob);
    const url     = await new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => {
          const fileProgress = snap.bytesTransferred / snap.totalBytes;
          const overall      = Math.round(((i + fileProgress) / blobs.length) * 100);
          onProgress?.(overall);
        },
        (err) => {
          console.error(`[uploadJobImages] FAILED on file ${i + 1}:`,
            'code:', err.code, '| message:', err.message,
            '| serverResponse:', err.serverResponse, err);
          reject(err);
        },
        async () => {
          const dlUrl = await getDownloadURL(task.snapshot.ref);
          console.log(`[uploadJobImages] File ${i + 1} done:`, dlUrl);
          resolve(dlUrl);
        }
      );
    });
    urls.push(url);
  }
  onProgress?.(100);
  console.log('[uploadJobImages] All done — URLs:', urls);
  return urls;
};

// Write imageUrls array back to the job doc after upload
export const updateJobImageUrls = async (jobId, imageUrls) => {
  await updateDoc(doc(db, 'jobs', jobId), { imageUrls });
};

// ─── PROFILE PHOTO ────────────────────────────────────────────────────────────
// Uploads a profile photo to Storage at users/{uid}/profile-photo,
// then saves the download URL to the Firestore users doc as profilePhoto.
export const uploadProfilePhoto = async (uid, file, onProgress) => {
  const path    = `users/${uid}/profile-photo`;
  const fileRef = storageRef(storage, path);
  const task    = uploadBytesResumable(fileRef, file);

  const downloadUrl = await new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });

  await updateDoc(doc(db, 'users', uid), { profilePhoto: downloadUrl });
  return downloadUrl;
};

// ─── REFERRALS ────────────────────────────────────────────────────────────────
// Schema: { jobId, referrerId, referredId, referrerName, referredName,
//           jobTitle, pickup, status, createdAt }

// Get all verified carriers except the current user (for the referral picker)
export const getVerifiedCarriers = async (excludeUid) => {
  const q = query(
    collection(db, 'publicCarriers'),
    where('verified', '==', true)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(c => c.id !== excludeUid)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

// Create a referral doc and notify the referred carrier
export const createReferral = async ({ jobId, referrerId, referredId, referrerName, referredName, jobTitle, pickup }) => {
  await addDoc(collection(db, 'referrals'), {
    jobId,
    referrerId,
    referredId,
    referrerName,
    referredName,
    jobTitle,
    pickup,
    status:    'pending',
    createdAt: serverTimestamp(),
  });
  await createNotification(
    referredId,
    'referral_received',
    `🤝 ${referrerName} recommended you for a ${jobTitle} job in ${pickup}!`,
    jobId
  );
};

// Get all referrals where the user is the referrer (for stats)
export const getMyReferrals = async (uid) => {
  const q = query(collection(db, 'referrals'), where('referrerId', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Mark all pending referrals for a job as expired
// (called when a job is completed or cancelled with no referral win)
export const expireJobReferrals = async (jobId) => {
  const q = query(collection(db, 'referrals'), where('jobId', '==', jobId));
  const snap = await getDocs(q);
  const pending = snap.docs.filter(d => d.data().status === 'pending');
  await Promise.all(pending.map(d => updateDoc(d.ref, { status: 'expired' })));
};

// ─── WEEKLY CHALLENGES ────────────────────────────────────────────────────────

// Get the currently active weekly challenge (single doc at weeklyChallenges/active)
export const getActiveChallenge = async () => {
  const snap = await getDoc(doc(db, 'weeklyChallenges', 'active'));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

// Listen to the active challenge in real time
export const listenActiveChallenge = (callback) => {
  return onSnapshot(doc(db, 'weeklyChallenges', 'active'), snap => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
};

// Get a carrier's challenge progress doc (keyed by providerId)
export const getChallengeProgress = async (providerId) => {
  const snap = await getDoc(doc(db, 'challengeProgress', providerId));
  if (!snap.exists()) return null;
  return snap.data();
};

// Listen to a carrier's challenge progress in real time
export const listenChallengeProgress = (providerId, callback) => {
  return onSnapshot(doc(db, 'challengeProgress', providerId), snap => {
    callback(snap.exists() ? snap.data() : null);
  });
};

// Challenge progress and rewards are written by Cloud Functions (functions/gamification.js).
// The weekly challenge itself is picked by the weeklyChallengePick schedule; clients
// can no longer write weeklyChallenges or challengeProgress. Kept as a no-op so
// existing callers keep working.
export const ensureActiveChallengeExists = async () => {};

// ─── ADMIN — CUSTOMERS ────────────────────────────────────────────────────────

// Fetch all registered customers — admin only (enforced by Firestore rules)
export const getCustomers = async () => {
  const q = query(
    collection(db, 'users'),
    where('type', '==', 'customer'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── ERROR LOGS (admin viewer) ────────────────────────────────────────────────

// Fetch most-recent error logs — admin only (enforced by Firestore rules)
export const getErrorLogs = async (limitCount = 50) => {
  const q = query(
    collection(db, 'errorLogs'),
    orderBy('timestamp', 'desc'),
    firestoreLimit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Delete a single error log entry
export const deleteErrorLog = async (logId) => {
  await deleteDoc(doc(db, 'errorLogs', logId));
};

// Delete ALL error log entries (batch, max 500 per Firestore batch — fetches all first)
export const deleteAllErrorLogs = async () => {
  const snap = await getDocs(collection(db, 'errorLogs'));
  if (snap.empty) return 0;
  // Firestore batches are capped at 500 ops; chunk if needed
  const chunks = [];
  for (let i = 0; i < snap.docs.length; i += 500) {
    chunks.push(snap.docs.slice(i, i + 500));
  }
  await Promise.all(chunks.map(chunk => {
    const batch = writeBatch(db);
    chunk.forEach(d => batch.delete(d.ref));
    return batch.commit();
  }));
  return snap.size;
};
